/**
 * Modern Bold · <mb-collection-stage> custom element
 * ─────────────────────────────────────────────────────────────────────────
 * Full-bleed collection slideshow: a filmstrip tablist, prev/next arrows and
 * an optional auto-advance. The active tab's progress bar is the timer: its
 * CSS animation ends → next slide, so pausing the animation (hover, focus
 * inside, hidden tab, off screen, the pause button) pauses the slideshow.
 * Reduced motion removes the animation, which removes auto-advance with it.
 *
 * DOM contract (rendered by sections/modern-collection-stage.liquid):
 *   <mb-collection-stage data-autoplay="true|false">
 *     [data-mb-stage-media="i"] × N   stacked photos; .is-active is shown
 *     [data-mb-stage-panel="i"] × N   slide copy; [hidden] when inactive
 *     [data-mb-stage-tab="i"] × N     filmstrip tabs (role="tab")
 *     [data-mb-stage-prev|next]       arrows (optional)
 *     [data-mb-stage-pause]           pause/play toggle (autoplay only)
 *     [data-mb-stage-current]         counter text "01"
 *     [data-mb-stage-live]            panels wrapper; aria-live is managed here
 */
if (!customElements.get('mb-collection-stage')) {
  class MBCollectionStage extends HTMLElement {
    #cleanups = [];
    #media = [];
    #panels = [];
    #tabs = [];
    #currentEl = null;
    #pauseBtn = null;
    #live = null;
    #io = null;
    #current = 0;
    #autoplay = false;
    #userPaused = false;
    #hovered = false;
    #focused = false;
    #hidden = false;
    #offscreen = false;

    connectedCallback() {
      this.#media = [...this.querySelectorAll('[data-mb-stage-media]')];
      this.#panels = [...this.querySelectorAll('[data-mb-stage-panel]')];
      this.#tabs = [...this.querySelectorAll('[data-mb-stage-tab]')];
      this.#currentEl = this.querySelector('[data-mb-stage-current]');
      this.#pauseBtn = this.querySelector('[data-mb-stage-pause]');
      this.#live = this.querySelector('[data-mb-stage-live]');
      if (!this.#tabs.length) return;

      this.#autoplay = this.dataset.autoplay === 'true' && this.#tabs.length > 1;
      const selected = this.#tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      this.#current = selected < 0 ? 0 : selected;

      this.#tabs.forEach((tab, i) => {
        this.#on(tab, 'click', () => this.goTo(i));
        this.#on(tab, 'keydown', (e) => this.#onTabKey(e, i));
      });
      const prev = this.querySelector('[data-mb-stage-prev]');
      const next = this.querySelector('[data-mb-stage-next]');
      if (prev) this.#on(prev, 'click', () => this.goTo(this.#current - 1));
      if (next) this.#on(next, 'click', () => this.goTo(this.#current + 1));
      if (this.#pauseBtn) this.#on(this.#pauseBtn, 'click', () => this.#setUserPaused(!this.#userPaused));

      // Theme editor: selecting a block shows its slide and holds it there.
      if (window.Shopify?.designMode) {
        this.#on(document, 'shopify:block:select', (e) => {
          const panel = e.target?.closest?.('[data-mb-stage-panel]');
          if (!panel || !this.contains(panel)) return;
          this.goTo(Number(panel.dataset.mbStagePanel));
          this.#setUserPaused(true);
        });
        this.#on(document, 'shopify:block:deselect', (e) => {
          if (this.contains(e.target)) this.#setUserPaused(false);
        });
      }

      if (this.#autoplay) {
        this.#on(this, 'animationend', (e) => {
          if (e.target.classList?.contains('mb-stage__bar-fill') && !this.#isPaused()) {
            this.goTo(this.#current + 1);
          }
        });
        this.#on(this, 'pointerenter', () => { this.#hovered = true; this.#sync(); });
        this.#on(this, 'pointerleave', () => { this.#hovered = false; this.#sync(); });
        this.#on(this, 'focusin', () => { this.#focused = true; this.#sync(); });
        this.#on(this, 'focusout', (e) => {
          if (!this.contains(e.relatedTarget)) { this.#focused = false; this.#sync(); }
        });
        this.#on(document, 'visibilitychange', () => { this.#hidden = document.hidden; this.#sync(); });
        if ('IntersectionObserver' in window) {
          this.#io = new IntersectionObserver(([entry]) => {
            this.#offscreen = !entry.isIntersecting;
            this.#sync();
          }, { threshold: 0.2 });
          this.#io.observe(this);
        }
      }
      this.#sync();
    }

    disconnectedCallback() {
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
      this.#io?.disconnect();
      this.#io = null;
    }

    /** Show slide `index` (wraps around). */
    goTo(index) {
      const n = this.#tabs.length;
      const i = ((index % n) + n) % n;
      this.#current = i;
      this.#media.forEach((m, k) => m.classList.toggle('is-active', k === i));
      this.#panels.forEach((p, k) => { p.hidden = k !== i; });
      this.#tabs.forEach((t, k) => {
        t.setAttribute('aria-selected', k === i ? 'true' : 'false');
        t.tabIndex = k === i ? 0 : -1;
      });
      if (this.#currentEl) this.#currentEl.textContent = String(i + 1).padStart(2, '0');
    }

    #onTabKey(e, i) {
      const keys = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: this.#tabs.length - 1 };
      if (!(e.key in keys)) return;
      e.preventDefault();
      this.goTo(keys[e.key]);
      this.#tabs[this.#current].focus();
    }

    #setUserPaused(paused) {
      this.#userPaused = paused;
      this.#pauseBtn?.setAttribute('aria-pressed', String(paused));
      this.#pauseBtn?.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      this.#sync();
    }

    #isPaused() {
      return this.#userPaused || this.#hovered || this.#focused || this.#hidden || this.#offscreen;
    }

    // Announce slide changes only while the slideshow is not rotating on its own.
    #sync() {
      const paused = this.#isPaused();
      this.classList.toggle('is-paused', this.#autoplay && paused);
      this.#live?.setAttribute('aria-live', !this.#autoplay || paused ? 'polite' : 'off');
    }

    #on(target, type, handler) {
      target.addEventListener(type, handler);
      this.#cleanups.push(() => target.removeEventListener(type, handler));
    }
  }

  customElements.define('mb-collection-stage', MBCollectionStage);
}
